'use client';

import { useEffect, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { sendMetrics } from '../../services/ga.service';
import { TRIAL } from '../(early-access)/coilmind/content';

// Coilmind on the generator page (owner, 2026-10-12: "must catch the eye, have
// CTAs, more colour"). The same widgets can be generated inside TIA Portal by
// the Coilmind assistant, paid from the subscription's AI allowance instead of
// svghmi credits. Every claim comes from the Coilmind landing copy
// (app/(early-access)/coilmind/content.js: WinCC Unified screens, LAD/SCL,
// TIA Portal V17–V21, the trial terms) plus the widget tools shipping with this
// release; no numbers about the allowance — it is shared with everything else
// the assistant does. Colours are Coilmind's own (ui.jsx: the dark
// workstation band with the "coil" green).
const COILMIND_URL = '/coilmind';
// The landing's trial CTA target (TRIAL_HREF in app/(early-access)/coilmind/ui.jsx;
// not imported to keep the landing's chrome out of this page's bundle).
const TRIAL_HREF = '/coilmind/account?intent=trial';
const COILMIND_TOUR_URL = '/coilmind#tour';
const DISMISS_KEY = 'ag_coilmind_card_dismissed';
const LOGO_SRC = '/coilmind/coilmind-badge.svg';

const track = (placement) => sendMetrics('ai_coilmind_cta', { placement });

const BENEFITS = [
  {
    title: 'Widgets straight into your project',
    text: 'Describe the widget to the assistant in TIA Portal — it lands in your project, no download and import.',
  },
  {
    title: 'No svghmi credits',
    text: 'Generation comes out of your Coilmind AI allowance.',
  },
  {
    title: 'The same assistant does the rest',
    text: 'It writes LAD and SCL and builds WinCC Unified screens in the project you have open.',
  },
];

/** The two Coilmind buttons: the free trial, and the landing's tour. */
function CoilmindCtas({ placement, secondary = true }) {
  return (
    <div className="ag-cm-ctas">
      <Link href={TRIAL_HREF} className="ag-cm-cta" onClick={() => track(`${placement}_trial`)}>
        Try Coilmind free
      </Link>
      {secondary && (
        <Link href={COILMIND_TOUR_URL} className="ag-cm-cta-2" onClick={() => track(`${placement}_tour`)}>
          See how it works
        </Link>
      )}
    </div>
  );
}

/**
 * The full-width Coilmind band (after "How it works"): headline, three
 * benefits, the logo and both calls to action.
 */
export function CoilmindBand() {
  return (
    <section className="ag-cm-band" aria-labelledby="ag-cm-band-h">
      <div className="container ag-cm-band-in">
        <div className="ag-cm-band-copy">
          <span className="ag-cm-kicker">Coilmind · for TIA Portal V17–V21</span>
          <h2 className="ag-cm-h" id="ag-cm-band-h">Generate widgets right inside TIA Portal</h2>
          <ul className="ag-cm-benefits">
            {BENEFITS.map((b) => (
              <li key={b.title}>
                <b>{b.title}</b>
                <span>{b.text}</span>
              </li>
            ))}
          </ul>
          <CoilmindCtas placement="band" />
          {TRIAL?.micro && <p className="ag-cm-micro">{TRIAL.micro}</p>}
        </div>
        <div className="ag-cm-band-art" aria-hidden="true">
          <Image src={LOGO_SRC} alt="" width={168} height={168} />
        </div>
      </div>
    </section>
  );
}

/**
 * Coloured callout near a finished widget. Dismissible; the dismissal is
 * remembered in localStorage (best effort: private mode simply shows it again).
 */
export function CoilmindCard() {
  // Hidden until mounted: the stored dismissal is only readable on the client.
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch { /* storage unavailable — show it */ }
    // Reading a browser-only value after mount is the point of this effect.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!dismissed) setVisible(true);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try { window.localStorage.setItem(DISMISS_KEY, '1'); } catch { /* best effort */ }
  };

  return (
    <aside className="ag-cm-card" aria-label="Coilmind for TIA Portal">
      <button type="button" className="ag-cm-x" onClick={dismiss} aria-label="Dismiss">×</button>
      <Image className="ag-cm-card-logo" src={LOGO_SRC} alt="" width={48} height={48} />
      <div className="ag-cm-card-body">
        <div className="ag-cm-title">Working in TIA Portal? Get widgets without leaving it.</div>
        <p>
          With Coilmind the assistant puts generated widgets straight into your project —
          paid from your Coilmind AI allowance, not credits.
        </p>
        <CoilmindCtas placement="result_card" />
      </div>
    </aside>
  );
}

/** Highlighted row in the pricing block. */
export function CoilmindPricingLine() {
  return (
    <div className="ag-cm-row">
      <Image className="ag-cm-row-logo" src={LOGO_SRC} alt="" width={40} height={40} />
      <div className="ag-cm-row-text">
        <b>Coilmind subscribers: no credits needed.</b>
        <span>Generate inside TIA Portal from your Coilmind AI allowance.</span>
      </div>
      <Link href={COILMIND_URL} className="ag-cm-cta" onClick={() => track('pricing_row')}>
        See Coilmind
      </Link>
    </div>
  );
}
